import { useState } from "react";
import { Link } from "react-router-dom";
import { ListChecks, PlayCircle, Loader2, AlertCircle, ArrowUpRight, RefreshCw } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import PageHeader from "@/layout/PageHeader";
import { API, authHeaders } from "@/lib/api";
import { toast } from "sonner";


function StatusPill({ status }) {
  const map = {
    generated: { bg: "#EFF6FF", fg: "#1D4ED8", label: "Generated" },
    sent:      { bg: "#ECFDF5", fg: "#047857", label: "Sent" },
    pending:   { bg: "#F1F5F9", fg: "#475569", label: "Pending" },
    failed:    { bg: "#FEF2F2", fg: "#B91C1C", label: "Failed" },
    skipped:   { bg: "#F1F5F9", fg: "#64748B", label: "Skipped" },
  };
  const m = map[status] || map.pending;
  return (
    <span
      className="mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-full"
      style={{ background: m.bg, color: m.fg }}
    >
      {m.label}
    </span>
  );
}


async function fetchSurveys() {
  const r = await fetch(`${API}/weekly-surveys?limit=50`, {
    headers: authHeaders(),
  });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();
}


export default function WeeklySurveysList() {
  const [generating, setGenerating] = useState(false);

  const { data, isPending, isError, error, refetch, isFetching } = useQuery({
    queryKey: ["weekly-surveys"],
    queryFn: fetchSurveys,
    staleTime: 5 * 60_000,
    refetchInterval: 30_000,
  });
  const rows = data?.data ?? [];

  const generateNow = async () => {
    setGenerating(true);
    try {
      const r = await fetch(`${API}/weekly-surveys/generate`, {
        method: "POST",
        headers: authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ dry_run: false }),
      });
      const body = await r.json();
      if (!r.ok) throw new Error(body?.detail || `HTTP ${r.status}`);
      toast.success(`Survey generation started (job ${body.job_id}). Runs ~30-60s.`);
      setTimeout(() => refetch(), 8000);
    } catch (e) {
      toast.error(`Generate failed: ${e.message}`);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div data-testid="weekly-surveys-page">
      <PageHeader
        title="Weekly Survey"
        subtitle="Wed→Tue SGT window — one poll question + 4 options. Auto-delivered every Wednesday 08:00 SGT."
      />
      <div className="hm-surface p-4 md:p-6" data-testid="weekly-surveys-panel">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="text-xs text-hm-fg-muted leading-relaxed max-w-xl">
            Product 5 · Weekly Survey of the Week. One timely, commercially
            relevant poll question anchored to a trending industry theme.
            Options are mutually-exclusive conceptual lanes. Deduplicated against
            the last 365 days.
          </div>
          <button
            onClick={generateNow}
            disabled={generating}
            data-testid="weekly-surveys-generate-now"
            className="shrink-0 inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-hm-accent text-hm-bg text-xs font-medium hover:bg-hm-accent-dim disabled:opacity-40"
          >
            {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PlayCircle className="h-3.5 w-3.5" />}
            Generate survey now
          </button>
        </div>

        {isError && !data ? (
          <div
            className="p-6 rounded-lg border border-hm-border bg-hm-bg-1 flex items-start gap-4"
            data-testid="weekly-surveys-error"
          >
            <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" style={{ color: "#B45309" }} />
            <div className="flex-1 min-w-0">
              <div className="text-sm text-hm-fg font-medium">Unable to load surveys right now</div>
              <p className="mono text-[11px] text-hm-fg-muted mt-1">
                {error?.message || "The connection failed. This is usually transient."}
              </p>
              <button
                onClick={() => refetch()}
                disabled={isFetching}
                data-testid="weekly-surveys-retry"
                className="mt-3 inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-medium border border-hm-border text-hm-fg hover:border-hm-border-strong disabled:opacity-40"
              >
                {isFetching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                Retry
              </button>
            </div>
          </div>
        ) : rows.length === 0 && !isPending ? (
          <div className="py-10 text-center" data-testid="weekly-surveys-empty">
            <ListChecks className="h-6 w-6 text-hm-fg-subtle mx-auto mb-3" />
            <div className="mono text-[11px] uppercase tracking-widest text-hm-fg-subtle">
              No surveys yet. Click &quot;Generate survey now&quot; above.
            </div>
          </div>
        ) : (
          <div className="rounded-lg border border-hm-border overflow-hidden">
            <div className="grid grid-cols-[0.8fr_2fr_0.6fr_0.9fr_auto] gap-4 px-5 py-3 bg-hm-bg-1 mono text-[10px] uppercase tracking-widest text-hm-fg-subtle">
              <div>Date</div>
              <div>Question</div>
              <div>Status</div>
              <div>Created</div>
              <div></div>
            </div>
            <div className="divide-y divide-hm-border">
              {rows.map((r) => (
                <Link
                  key={r.id}
                  to={`/weekly-surveys/${r.id}`}
                  data-testid={`weekly-surveys-row-${r.id.slice(0,8)}`}
                  className="grid grid-cols-[0.8fr_2fr_0.6fr_0.9fr_auto] gap-4 px-5 py-4 items-center hover:bg-hm-bg-1/40 transition-colors"
                >
                  <div>
                    <div className="text-sm text-hm-fg font-medium">{r.survey_date}</div>
                    {r.anchor_topic && (
                      <div className="mono text-[10px] text-hm-fg-subtle mt-0.5 truncate">
                        {r.anchor_topic}
                      </div>
                    )}
                  </div>
                  <div className="text-sm text-hm-fg leading-snug line-clamp-2">
                    {r.question}
                  </div>
                  <div><StatusPill status={r.status} /></div>
                  <div className="mono text-[11px] text-hm-fg-muted">
                    {new Date(r.created_at).toLocaleString(undefined, {
                      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
                    })}
                  </div>
                  <div className="text-hm-fg-subtle">
                    <ArrowUpRight className="h-4 w-4" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
